// ============================================================================
// Adresses des fichiers de marque (logo, CGV) : traduction pure, sans React.
// ----------------------------------------------------------------------------
// Le serveur enregistre ces fichiers sous forme de chemin relatif
// « /uploads/... », qu'il ne sait servir que depuis api.vyndra.fr. Affiche tel
// quel dans le back-office (app.vyndra.fr), ce chemin donnait un HTTP 404 et
// l'icone d'image brisee. Toute adresse de fichier de marque doit donc passer
// par `resolveBrandAssetUrl`, qui la ramene sur notre domaine via le relais
// /api/brand-assets (voir la note detaillee dans ce relais).
// ============================================================================

// Mesure de contraste WCAG : sert à décider quelle variante de logo va sur un
// fond donné (voir « variantePourFond » plus bas). Module pur lui aussi.
import { contraste } from './color-utils';

/** Préfixe des fichiers de marque tels que stockés en base par le serveur. */
const PREFIXE_UPLOADS = '/uploads/';

/** Relais same-origin : satisfait « img-src 'self' » de la CSP du site. */
const PREFIXE_RELAIS = '/api/brand-assets/';

/**
 * Transforme l'adresse stockée en base en adresse réellement affichable depuis
 * le back-office.
 *
 * - « /uploads/brand-logos/a/b.png » devient « /api/brand-assets/brand-logos/a/b.png »
 * - une adresse absolue (http/https) est laissée intacte : elle vient d'ailleurs
 * - une valeur vide devient null, jamais une chaîne vide (un src="" recharge la page)
 */
export function resolveBrandAssetUrl(raw: string | null | undefined): string | null {
  const valeur = (raw ?? '').trim();
  if (!valeur) return null;
  if (valeur.startsWith('http://') || valeur.startsWith('https://')) return valeur;
  if (valeur.startsWith(PREFIXE_UPLOADS)) {
    return PREFIXE_RELAIS + valeur.slice(PREFIXE_UPLOADS.length);
  }
  // Forme inattendue : on ne fabrique pas une adresse au hasard. L'appelant
  // affichera son repli et dira que l'adresse est inexploitable.
  return null;
}

/** Vrai si l'adresse stockée existe mais n'est pas exploitable telle quelle. */
export function isBrandAssetUrlUnusable(raw: string | null | undefined): boolean {
  const valeur = (raw ?? '').trim();
  return valeur.length > 0 && resolveBrandAssetUrl(valeur) === null;
}

// ============================================================================
// LES QUATRE VARIANTES DE LOGO, ET LA RÈGLE DE REPLI
// ----------------------------------------------------------------------------
// POURQUOI. Jusqu'au 06/08/2026 une identité ne portait qu'UN logo. Le même
// fichier servait le mail sur fond blanc, le bandeau sombre de l'application et
// l'icône carrée de l'application installée. Le dirigeant avait déposé sa
// version foncée, faite pour le fond blanc des mails : sur le bandeau sombre
// elle disparaissait, et dans un carré un logo en longueur ne tient pas.
//
// Le découpage est une matrice « forme x fond » :
//
//                       FOND CLAIR                    FOND SOMBRE
//   EN LONGUEUR   logoUrl                        logoDarkUrl
//                 mails, en-tête de facture,     bandeau d'espace, application,
//                 plaquette, signature           connexion à l'espace
//
//   CARRÉ 1:1     logoSquareUrl                  logoSquareDarkUrl
//                 vignette sur document clair    icône installée, badge, avatar
//
// RÈGLE DE REPLI : personne n'est obligé de déposer quatre fichiers. Avec un
// seul, tout continue de fonctionner. Ordre de recherche :
//   1. la variante exacte ;
//   2. l'AUTRE FORME, sur le MÊME FOND ;
//   3. la même forme, sur l'autre fond ;
//   4. n'importe laquelle, dans un ordre stable.
//
// Le FOND prime volontairement sur la FORME. Se tromper de fond est un défaut
// GRAVE : un logo dessiné pour le blanc, posé sur un bandeau sombre, peut
// devenir invisible. Se tromper de forme n'est qu'une question d'élégance : un
// logo en longueur dans un carré reste lisible, simplement plus petit (tous les
// affichages sont en « contain », rien n'est déformé ni rogné).
//
// Cette règle est écrite DEUX FOIS : ici, et dans
// apps/backend/Services/BrandTheme/BrandLogoVariants.cs. C'est assumé, le
// serveur en a besoin pour les mails et PDF qu'il génère seul. Les deux
// implémentations doivent rester alignées.
// ============================================================================

export type FormeLogo = 'longueur' | 'carre';
export type FondLogo = 'clair' | 'sombre';

export type VarianteLogo =
  | 'longueur-clair'
  | 'longueur-sombre'
  | 'carre-clair'
  | 'carre-sombre';

/** Les quatre adresses telles que renvoyées par le serveur (chemins relatifs). */
export interface JeuLogos {
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  logoSquareUrl?: string | null;
  logoSquareDarkUrl?: string | null;
}

export interface DescriptionVariante {
  cle: VarianteLogo;
  forme: FormeLogo;
  fond: FondLogo;
  /** Libellé court, celui du titre de l'emplacement. */
  libelle: string;
  /** Où ce fichier sert réellement, en langage du métier. */
  usage: string;
  /**
   * NOM DE L'EMPLACEMENT PAR SA DESTINATION LA PLUS VISIBLE.
   *
   * POURQUOI CE CHAMP EXISTE. Reproche du dirigeant le 08/08/2026 : « impossible
   * de savoir où est-ce que je peux apporter une modif, pour que ça marche ».
   * « En longueur, fond clair » décrit la FORME du fichier, pas l'endroit où on
   * le verra. Quatre emplacements décrits par leur géométrie, c'est quatre
   * devinettes. Chaque emplacement porte donc désormais le nom de ce qu'il
   * peint à l'écran, et la géométrie passe en second.
   */
  titre: string;
  /**
   * Les endroits EXACTS, dans le logiciel, où ce fichier apparaît. Écrits en
   * français simple, pas en noms de composants : c'est ce qui permet de vérifier
   * qu'on a déposé le bon fichier au bon endroit.
   */
  destinations: readonly string[];
  /**
   * Ce que le fichier doit CONTENIR, quand ce n'est pas évident. Vérifié le
   * 08/08/2026 : le carré déposé pour Guard Security était le logo COMPLET,
   * nom compris. Résultat, en tête de menu le nom était écrit deux fois, une
   * fois dans l'image et une fois par l'application. Personne ne le lui avait dit.
   */
  contenuAttendu: string | null;
}

export const VARIANTES_LOGO: readonly DescriptionVariante[] = [
  {
    cle: 'longueur-clair',
    forme: 'longueur',
    fond: 'clair',
    libelle: 'Logo allongé POUR FOND CLAIR (documents, courriels)',
    titre: 'Logo des mails et des factures',
    usage: 'Mails, haut de facture, plaquette',
    destinations: [
      'En-tête des mails envoyés aux clients (relances, factures)',
      'Haut des factures et devis en PDF',
      'Plaquette commerciale',
    ],
    contenuAttendu: 'Le logo COMPLET, avec le nom écrit dedans : il est seul sur un fond blanc.',
  },
  {
    cle: 'longueur-sombre',
    forme: 'longueur',
    fond: 'sombre',
    libelle: 'Logo allongé POUR FOND SOMBRE (application)',
    // C'EST L'EMPLACEMENT DE LA TETE DE MENU depuis le 09/08/2026. Décision du
    // dirigeant : « celui une fois DANS l'univers au-dessus du menu doit être au
    // format en LONGUEUR ». Avant, la tête de menu tirait sur le fichier CARRÉ,
    // donc sur le même que la pastille d'entrée : régler l'un cassait l'autre.
    titre: "Logo en tête du menu de l'univers",
    usage: "Tête du menu, bandeau de l'espace",
    destinations: [
      "En HAUT du menu de gauche, une fois dans l'univers",
      "Bandeau haut de l'espace de travail",
      "Page de connexion à l'espace",
    ],
    contenuAttendu:
      'Le logo COMPLET avec le nom écrit dedans, dessiné pour ressortir sur du '
      + "sombre (traits clairs). L'application n'écrit pas le nom à côté : ce "
      + 'fichier doit donc le porter.',
  },
  {
    cle: 'carre-clair',
    forme: 'carre',
    fond: 'clair',
    libelle: 'Logo carré POUR FOND CLAIR',
    titre: 'Blason sur un document clair',
    usage: 'Vignette sur document clair',
    destinations: ['Vignette carrée posée sur un document ou un fond blanc'],
    contenuAttendu: 'Le blason SEUL, sans le nom écrit dedans.',
  },
  {
    cle: 'carre-sombre',
    forme: 'carre',
    fond: 'sombre',
    libelle: 'Logo carré POUR FOND SOMBRE',
    // C'EST L'EMPLACEMENT PAR LEQUEL ON ENTRE DANS UN UNIVERS, et il est nommé
    // par ce qu'il peint. Décision du dirigeant du 09/08/2026 : « celui pour
    // RENTRER dans l'univers doit être celui au format CARRÉ ». Il ne peint plus
    // la tête de menu, qui a désormais son propre fichier (en longueur) et ses
    // propres réglages.
    titre: "Blason pour entrer dans l'univers",
    usage: "Carte du lanceur, liste des espaces, icône installée",
    destinations: [
      "Pastille de la carte de l'univers sur l'écran de choix d'espace",
      "Vignette de l'univers dans la liste « Changer d'espace »",
      "Icône de l'application installée sur téléphone",
    ],
    contenuAttendu:
      'Le BLASON SEUL, en PNG à fond transparent. Pas le nom : il est déjà écrit '
      + 'juste à côté, sur la carte comme dans la liste. Un fichier qui contient '
      + 'le nom le fait apparaître DEUX fois, et réduit à 36 px ce nom ne se lit '
      + 'plus.',
  },
] as const;

export function decrireVariante(cle: VarianteLogo): DescriptionVariante {
  return VARIANTES_LOGO.find((v) => v.cle === cle) ?? VARIANTES_LOGO[0];
}

/** Adresse BRUTE stockée pour une variante, sans aucun repli. */
export function logoBrut(jeu: JeuLogos | null | undefined, cle: VarianteLogo): string | null {
  if (!jeu) return null;
  const valeur =
    cle === 'longueur-clair' ? jeu.logoUrl
      : cle === 'longueur-sombre' ? jeu.logoDarkUrl
        : cle === 'carre-clair' ? jeu.logoSquareUrl
          : jeu.logoSquareDarkUrl;
  const propre = (valeur ?? '').trim();
  return propre.length > 0 ? propre : null;
}

/** Ordre de recherche : exacte, autre forme même fond, même forme autre fond, dernière. */
export function ordreDeRepli(demandee: VarianteLogo): VarianteLogo[] {
  switch (demandee) {
    case 'longueur-clair':
      return ['longueur-clair', 'carre-clair', 'longueur-sombre', 'carre-sombre'];
    case 'longueur-sombre':
      return ['longueur-sombre', 'carre-sombre', 'longueur-clair', 'carre-clair'];
    case 'carre-clair':
      return ['carre-clair', 'longueur-clair', 'carre-sombre', 'longueur-sombre'];
    case 'carre-sombre':
      return ['carre-sombre', 'longueur-sombre', 'carre-clair', 'longueur-clair'];
    default:
      return ['longueur-clair', 'carre-clair', 'longueur-sombre', 'carre-sombre'];
  }
}

export interface LogoResolu {
  /** Adresse AFFICHABLE (déjà passée par le relais same-origin), ou null. */
  url: string | null;
  /** Adresse telle que stockée par le serveur, pour les diagnostics. */
  brut: string | null;
  demandee: VarianteLogo;
  /** Variante réellement servie. Null quand aucun fichier n'existe. */
  servie: VarianteLogo | null;
  /** Vrai quand la variante demandée manque et qu'une autre a pris sa place. */
  estRepli: boolean;
  /** Aucun fichier d'aucune sorte n'est enregistré sur cette identité. */
  aucun: boolean;
  /** Vrai si le fichier servi existe mais que son adresse est inexploitable. */
  inexploitable: boolean;
}

/**
 * Rend le meilleur fichier disponible pour l'usage demandé, ET DIT LEQUEL.
 * L'écran doit pouvoir afficher « Reprend : en longueur, fond clair » : sans
 * cela l'utilisateur ne comprend pas pourquoi son icône carrée ressemble à son
 * en-tête de mail.
 */
export function resoudreLogo(
  jeu: JeuLogos | null | undefined,
  demandee: VarianteLogo,
): LogoResolu {
  for (const candidate of ordreDeRepli(demandee)) {
    const brut = logoBrut(jeu, candidate);
    if (brut) {
      return {
        url: resolveBrandAssetUrl(brut),
        brut,
        demandee,
        servie: candidate,
        estRepli: candidate !== demandee,
        aucun: false,
        inexploitable: isBrandAssetUrlUnusable(brut),
      };
    }
  }
  return {
    url: null,
    brut: null,
    demandee,
    servie: null,
    estRepli: false,
    aucun: true,
    inexploitable: false,
  };
}

// ============================================================================
// QUEL LOGO À QUEL ENDROIT : LE CHOIX DU DIRIGEANT, EMPLACEMENT PAR EMPLACEMENT
// ----------------------------------------------------------------------------
// POURQUOI (19/09/2026). Plainte du dirigeant, répétée : « on a plusieurs logos
// de différentes couleurs et formats, carrés ou allongés, avec un contraste sur
// fond clair ou sur fond foncé. Tu m'affiches le logo fait pour fond clair : le
// contour de Guard Informatique est gris sur fond noir, il ne se voit pas. Je
// n'ai pas la main de manière très granulaire sur à quel endroit j'utilise tel
// ou tel logo. »
//
// Vérifié en production : la pastille du menu de guardsecurity.vyndra.fr
// affichait le fichier « carré, fond clair » (contour gris) sur fond noir.
//
// LA RÉPONSE, EN DEUX PIÈCES :
//   1. EMPLACEMENTS_LOGO : la table des emplacements RÉELS de l'application,
//      avec pour chacun le fond réellement posé derrière le logo, la forme
//      attendue et la variante servie par défaut ;
//   2. brand_themes.logo_emplacements (JSONB, servi en `logoEmplacements`) :
//      le choix du dirigeant, { "menu_tete": "longueur-sombre", ... }.
//
// UN SEUL POINT DE DÉCISION : logoPourEmplacement(jeu, emplacement, reglages).
// La variante est celle du réglage quand il existe et n'est pas « auto », sinon
// celle par défaut de la table. Puis le repli RESTE SUR LE MÊME FOND : un
// emplacement sombre ne retombe JAMAIS sur une variante claire. S'il n'existe
// rien de sombre, l'appelant affiche son monogramme ou son nom. C'est le coeur
// de la plainte : un logo pour fond clair posé sur du noir est pire qu'un nom
// écrit en toutes lettres.
//
// Cette table est écrite DEUX FOIS : ici et dans
// apps/backend/Services/BrandTheme/LogoEmplacements.cs. C'est assumé : le
// serveur en a besoin pour les PDF et les courriels qu'il fabrique seul. Un
// test serveur relit ce fichier et compare les deux tables.
// ============================================================================

export type EmplacementLogo =
  | 'menu_tete'
  | 'menu_pastille'
  | 'changement_espace'
  | 'lanceur_carte'
  | 'connexion'
  | 'pwa_icone'
  | 'univers_liste'
  | 'filigrane'
  | 'entete_application'
  | 'commercial_barre'
  | 'plaquette_atelier'
  | 'liste_societes'
  | 'pdf_contrat'
  | 'pdf_facture'
  | 'pdf_rh'
  | 'courriel'
  | 'courriel_pastille'
  | 'courriel_carre'
  | 'signature_publique'
  | 'plaquette_claire'
  | 'plaquette_sombre';

/** « auto » = le logiciel choisit, c'est-à-dire le défaut de la table. */
export type ReglageEmplacement = VarianteLogo | 'auto';

/** Le choix du dirigeant, tel que servi par le serveur (`logoEmplacements`). */
export type ReglagesEmplacements = Partial<Record<EmplacementLogo, ReglageEmplacement>>;

export interface DescriptionEmplacement {
  cle: EmplacementLogo;
  /** Libellé français, celui de la ligne de réglage. */
  libelle: string;
  /** Le fond RÉELLEMENT posé derrière le logo à cet endroit. */
  fond: FondLogo;
  /** La forme attendue par le cadre. */
  forme: FormeLogo;
  /** La variante servie quand le dirigeant n'a rien choisi. */
  defaut: VarianteLogo;
  /** Où ça se trouve, en langage du métier, pour l'écran de réglages. */
  ou: string;
}

/**
 * La table des emplacements. L'ordre est celui de l'écran de réglages :
 * l'application d'abord (là où la plainte est née), les documents ensuite.
 */
export const EMPLACEMENTS_LOGO: readonly DescriptionEmplacement[] = [
  { cle: 'menu_tete', libelle: "Tête du menu de l'espace", fond: 'sombre', forme: 'longueur', defaut: 'longueur-sombre', ou: "En haut du menu de gauche, une fois dans l'espace" },
  { cle: 'menu_pastille', libelle: "Pastille de l'établissement dans le menu (28 px)", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "La carte d'établissement sous la tête de menu" },
  { cle: 'changement_espace', libelle: "Vignette de la liste « Changer d'espace » (36 px)", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: 'La liste qui bascule entre les espaces' },
  { cle: 'lanceur_carte', libelle: "Carte du lanceur d'espaces", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "La pastille de la carte sur l'écran de choix d'espace" },
  { cle: 'connexion', libelle: "Écran d'identification (connexion, code, mot de passe)", fond: 'sombre', forme: 'longueur', defaut: 'longueur-sombre', ou: "La carte sombre de l'écran de connexion" },
  { cle: 'pwa_icone', libelle: "Icône de l'application installée", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "L'icône sur l'écran d'accueil du téléphone" },
  { cle: 'univers_liste', libelle: 'Vignette des univers dans les réglages (28 px)', fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: 'Réglages, Univers' },
  { cle: 'filigrane', libelle: "Filigrane en fond d'écran (silhouette)", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "Le grand dessin estompé derrière l'écran de connexion et l'ouverture d'un espace" },
  { cle: 'entete_application', libelle: "Barre du haut de l'application et vignette de société", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: 'Le bloc « société » en haut du menu de gauche, la liste des sociétés et les tuiles du tableau de bord' },
  { cle: 'commercial_barre', libelle: "Barre du haut de l'espace commercial", fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "Le bandeau qui rappelle l'espace dans lequel on travaille, en haut de l'espace commercial" },
  { cle: 'plaquette_atelier', libelle: 'Atelier de plaquette : vignette du bandeau et des cartes', fond: 'sombre', forme: 'carre', defaut: 'carre-sombre', ou: "L'écran où l'on compose une plaquette : le bandeau de l'espace et les cartes des plaquettes enregistrées" },
  { cle: 'liste_societes', libelle: 'Vignette de la liste des sociétés (réglages)', fond: 'clair', forme: 'carre', defaut: 'carre-clair', ou: 'Réglages, Sociétés émettrices' },
  { cle: 'pdf_contrat', libelle: 'En-tête du PDF de contrat', fond: 'clair', forme: 'longueur', defaut: 'longueur-clair', ou: 'Le contrat envoyé à la signature' },
  { cle: 'pdf_facture', libelle: 'Haut des factures et devis PDF', fond: 'clair', forme: 'longueur', defaut: 'longueur-clair', ou: 'Les documents téléchargés' },
  { cle: 'pdf_rh', libelle: 'En-tête des PDF RH (demandes, avenants)', fond: 'clair', forme: 'carre', defaut: 'carre-clair', ou: 'Les PDF produits par le module salariés' },
  { cle: 'courriel', libelle: 'En-tête des courriels', fond: 'clair', forme: 'longueur', defaut: 'longueur-clair', ou: 'Relances, factures envoyées, courriels du logiciel' },
  { cle: 'courriel_pastille', libelle: 'Pastille à côté du nom dans les courriels (32 px)', fond: 'clair', forme: 'carre', defaut: 'carre-clair', ou: "L'en-tête des courriels de service" },
  { cle: 'courriel_carre', libelle: 'Blason à droite du bandeau des courriels', fond: 'clair', forme: 'carre', defaut: 'carre-clair', ou: "Le symbole seul, à l'autre bout du bandeau des courriels" },
  { cle: 'signature_publique', libelle: 'En-tête de la page de signature du client (bandeau noir)', fond: 'sombre', forme: 'longueur', defaut: 'longueur-sombre', ou: "Le bandeau du haut de la page que le client ouvre pour signer : il est NOIR (#16181C), pas blanc" },
  { cle: 'plaquette_claire', libelle: "Plaquette d'offres, version blanche", fond: 'clair', forme: 'longueur', defaut: 'longueur-clair', ou: 'La plaquette commerciale sur fond blanc' },
  { cle: 'plaquette_sombre', libelle: "Plaquette d'offres, version colonnes", fond: 'sombre', forme: 'longueur', defaut: 'longueur-sombre', ou: 'La plaquette commerciale sur fond sombre' },
] as const;

const VALEURS_REGLAGE: readonly string[] = ['auto', 'longueur-clair', 'longueur-sombre', 'carre-clair', 'carre-sombre'];

export function estEmplacementLogo(cle: unknown): cle is EmplacementLogo {
  return typeof cle === 'string' && EMPLACEMENTS_LOGO.some((e) => e.cle === cle);
}

export function estReglageEmplacement(valeur: unknown): valeur is ReglageEmplacement {
  return typeof valeur === 'string' && VALEURS_REGLAGE.includes(valeur);
}

export function decrireEmplacement(cle: EmplacementLogo): DescriptionEmplacement {
  return EMPLACEMENTS_LOGO.find((e) => e.cle === cle) ?? EMPLACEMENTS_LOGO[0];
}

/**
 * Lit `logoEmplacements` tel que le serveur le renvoie. Tolérant : une valeur
 * absente ou illisible donne un réglage vide (« le logiciel choisit »), et une
 * entrée inconnue est ignorée seule, jamais le réglage entier.
 */
export function lireReglagesEmplacements(brut: unknown): ReglagesEmplacements {
  const resultat: ReglagesEmplacements = {};
  if (!brut || typeof brut !== 'object' || Array.isArray(brut)) return resultat;
  for (const [cle, valeur] of Object.entries(brut as Record<string, unknown>)) {
    if (!estEmplacementLogo(cle)) continue;
    const v = typeof valeur === 'string' ? valeur.trim().toLowerCase() : '';
    if (estReglageEmplacement(v)) resultat[cle] = v;
  }
  return resultat;
}

/** La variante « forme x fond » composée à partir de ses deux axes. */
export function composerVariante(forme: FormeLogo, fond: FondLogo): VarianteLogo {
  return forme === 'carre'
    ? (fond === 'sombre' ? 'carre-sombre' : 'carre-clair')
    : (fond === 'sombre' ? 'longueur-sombre' : 'longueur-clair');
}

export function formeDeVariante(v: VarianteLogo): FormeLogo {
  return v === 'carre-clair' || v === 'carre-sombre' ? 'carre' : 'longueur';
}

export function fondDeVariante(v: VarianteLogo): FondLogo {
  return v === 'longueur-sombre' || v === 'carre-sombre' ? 'sombre' : 'clair';
}

/**
 * La variante que CET emplacement doit demander.
 *
 *  - le réglage du dirigeant, s'il existe et n'est pas « auto » ;
 *  - sinon la forme `formeSiAuto` quand l'emplacement a un réglage de
 *    présentation propre (tête de menu et entrée d'univers : « blason » ou
 *    « large », « carré » ou « long »), posée sur le fond de la table ;
 *  - sinon le défaut de la table.
 */
export function varianteDeLEmplacement(
  emplacement: EmplacementLogo,
  reglages: ReglagesEmplacements | null | undefined,
  formeSiAuto?: FormeLogo,
): VarianteLogo {
  const description = decrireEmplacement(emplacement);
  const regle = reglages?.[emplacement];
  if (regle && regle !== 'auto') return regle;
  if (formeSiAuto) return composerVariante(formeSiAuto, description.fond);
  return description.defaut;
}

/**
 * Ordre de recherche QUI RESTE SUR LE MÊME FOND : la variante exacte, puis
 * l'autre forme sur le même fond. Jamais l'autre fond.
 */
export function ordreDeRepliMemeFond(demandee: VarianteLogo): VarianteLogo[] {
  switch (demandee) {
    case 'longueur-clair': return ['longueur-clair', 'carre-clair'];
    case 'longueur-sombre': return ['longueur-sombre', 'carre-sombre'];
    case 'carre-clair': return ['carre-clair', 'longueur-clair'];
    case 'carre-sombre': return ['carre-sombre', 'longueur-sombre'];
    default: return ['longueur-clair', 'carre-clair'];
  }
}

/** Le fichier à servir pour une variante, sans jamais changer de fond. */
export function resoudreLogoMemeFond(
  jeu: JeuLogos | null | undefined,
  demandee: VarianteLogo,
): LogoResolu {
  for (const candidate of ordreDeRepliMemeFond(demandee)) {
    const brut = logoBrut(jeu, candidate);
    if (brut) {
      return {
        url: resolveBrandAssetUrl(brut),
        brut,
        demandee,
        servie: candidate,
        estRepli: candidate !== demandee,
        aucun: false,
        inexploitable: isBrandAssetUrlUnusable(brut),
      };
    }
  }
  return { url: null, brut: null, demandee, servie: null, estRepli: false, aucun: true, inexploitable: false };
}

export interface LogoEmplacementResolu extends LogoResolu {
  emplacement: EmplacementLogo;
  /** Vrai quand la variante vient du réglage du dirigeant, pas du défaut. */
  choisie: boolean;
}

/**
 * LE point de décision pour tous les emplacements de l'application.
 * Réglage ou défaut, puis repli sur le même fond. Voir l'en-tête du bloc.
 */
export function logoPourEmplacement(
  jeu: JeuLogos | null | undefined,
  emplacement: EmplacementLogo,
  reglages: ReglagesEmplacements | null | undefined,
  formeSiAuto?: FormeLogo,
): LogoEmplacementResolu {
  const regle = reglages?.[emplacement];
  const variante = varianteDeLEmplacement(emplacement, reglages, formeSiAuto);
  return {
    ...resoudreLogoMemeFond(jeu, variante),
    emplacement,
    choisie: !!regle && regle !== 'auto',
  };
}

/**
 * COMMENT POSER UN FICHIER DANS UN EMPLACEMENT CARRÉ, QUAND IL N'EST PAS CARRÉ.
 *
 * DÉFAUT CONSTATÉ LE 08/08/2026, DEUX ÉCRANS, MÊME SYMPTÔME. En tête du menu de
 * l'univers Informatique ET sur la carte du lanceur, on voyait un MONOGRAMME
 * (« GI » sur une plaque) alors qu'un fichier de logo existe bel et bien en base.
 * Vérifié : l'identité « Guard Informatique » ne porte qu'un seul fichier,
 * `logo_url`, de 898 x 1402 pixels, et aucune variante carrée. La règle de repli
 * le servait correctement ; c'est l'ÉCRAN qui le refusait ensuite, au motif
 * qu'il n'était pas carré, et retombait sur les initiales.
 *
 * LA RÈGLE POSÉE ICI, ET ELLE VAUT PARTOUT : le monogramme est un DERNIER
 * recours, réservé au cas où AUCUN fichier n'existe. Dès qu'un fichier existe,
 * il sort. Ce qui change, c'est la façon de le poser, et elle dépend de sa forme
 * RÉELLE, mesurée sur l'image chargée, jamais supposée :
 *
 *  - « bandeau » : le fichier est nettement plus large que haut. Il porte donc
 *    presque toujours le nom écrit dedans. On l'étale sur toute la largeur
 *    disponible, à la hauteur de l'emplacement, et l'appelant N'ÉCRIT PAS le nom
 *    à côté : il serait là deux fois.
 *  - « blason » : le fichier est carré, presque carré, ou plus haut que large. Il
 *    tient dans le carré de l'emplacement, sans déformation ni rognage, et le
 *    nom est écrit à côté.
 *
 * Un fichier plus HAUT que large reste petit dans un carré, et c'est inévitable :
 * on ne peut pas le rogner sans décider à la place de l'utilisateur quelle
 * partie garder. L'écran de réglages le dit, et propose de fabriquer le carré.
 */
export type PoseLogo = 'blason' | 'bandeau';

export function poseSelonRatio(ratio: number | null): PoseLogo {
  if (ratio === null || !Number.isFinite(ratio) || ratio <= 0) return 'blason';
  return ratio >= RATIO_LONGUEUR_MIN ? 'bandeau' : 'blason';
}

// ============================================================================
// LE MONOGRAMME D'UNE MARQUE : SUPPRIMÉ LE 21/09/2026. NE PAS LE REMETTRE.
// ----------------------------------------------------------------------------
// `initialesMarque(nom)` vivait ici : deux caractères tirés du nom de l'identité
// (« Guard Security » donnait GS), affichés dans une plaque de couleur partout
// où aucun fichier de logo ne convenait au fond de l'emplacement.
//
// POURQUOI IL N'EXISTE PLUS. Plainte du dirigeant, capture à l'appui : « il y a
// un vrai logo dans les réglages en PNG, ce n'est pas pour que tu m'inventes je
// ne sais quoi... même pour Vyndra tu m'inventes un « V » chelou, alors qu'on a
// un logo propre. » Un monogramme n'est pas un repli, c'est une marque inventée,
// et il masquait en plus le vrai problème : un emplacement qui ne trouve aucun
// fichier pour SON fond doit se voir, pas se maquiller.
//
// CE QUI LE REMPLACE, PARTOUT :
//   - le fichier de l'emplacement (logoPourEmplacement), réglé dans
//     Réglages, Sociétés émettrices, « Où chaque logo est utilisé » ;
//   - à défaut, LE NOM DE LA MARQUE EN TOUTES LETTRES ;
//   - et rien du tout quand ce nom est déjà écrit juste à côté, plutôt que de
//     l'écrire deux fois dans une pastille de 28 px.
//
// Un garde-fou le vérifie : apps/web/scripts/verifier-logos.mjs, appelé par
// « pnpm verifier-avant-livraison ».
//
// SEULE EXCEPTION, ET ELLE EST EXPLICITE : le filigrane des cartes du lanceur
// (FiligraneInitiales, components/espace/lanceur-carte.tsx) garde des initiales,
// parce que le dirigeant les y a validées mot pour mot : « les filigranes en
// initiales, c'était ok pour moi, pour changer un peu ». Elles y sont un décor
// de fond, jamais le portrait de la marque.
// ============================================================================

/** Combien de variantes sont réellement déposées, sur les quatre possibles. */
export function compterLogosDeposes(jeu: JeuLogos | null | undefined): number {
  return VARIANTES_LOGO.filter((v) => logoBrut(jeu, v.cle) !== null).length;
}

// ============================================================================
// CE QUI MANQUE, ET CE QUE ÇA PROVOQUE : DIT EN FRANÇAIS, PAS EN NOM DE COLONNE
// ----------------------------------------------------------------------------
// POURQUOI. Le 08/08/2026, le dirigeant regarde la page de choix d'univers et
// demande : « Pourquoi l'effet du logo dans l'encart sur Guard Security est bon
// design, mais pas sur Guard Informatique, vieux fond blanc dégueulasse tout
// moche, sans effet profond ? »
//
// Il avait raison de trouver ça laid, et il ne pouvait pas savoir pourquoi :
// l'identité « Guard Informatique » ne porte qu'UN fichier sur quatre, dessiné en
// encre foncée pour un fond blanc. Sur une tuile sombre il disparaîtrait, donc
// l'application lui pose une plaque claire dessous pour le sauver. La plaque fait
// son travail, mais elle trahit un fichier absent. Guard Security, elle, porte
// les quatre fichiers : sa tuile est sombre et propre.
//
// Rien à l'écran ne le disait. Il en concluait un défaut du logiciel, alors qu'il
// lui manquait une image. D'où ces conseils : ils NOMMENT la variante utile, ce
// qu'elle change concrètement, et les endroits où ça se voit.
//
// TON : un conseil, pas une alarme. Une identité incomplète FONCTIONNE.
// PRIORITÉ : la variante pour fond sombre d'abord, c'est celle qui provoque la
// plaque claire. On ne réclame pas quatre fichiers à qui n'en a pas besoin.
// ============================================================================

export interface ConseilLogo {
  /** La variante à déposer. */
  cle: VarianteLogo;
  /** Ordre de traitement : 1 = à faire en premier. */
  priorite: number;
  /** Ce qui se passe aujourd'hui, faute de ce fichier. */
  consequence: string;
  /** Les endroits où cela se voit, en langage du métier. */
  ou: readonly string[];
}

/**
 * Les fichiers qui manquent VRAIMENT à cette identité, par ordre d'utilité.
 *
 * Une identité sans aucun fichier n'a pas besoin de quatre conseils : elle en a
 * un, déposer son logo. On ne rend donc rien dans ce cas, l'écran affiche déjà
 * partout que le nom est écrit en toutes lettres.
 */
export function conseilsLogos(jeu: JeuLogos | null | undefined): ConseilLogo[] {
  const aUnFichier = (cle: VarianteLogo) => logoBrut(jeu, cle) !== null;
  if (compterLogosDeposes(jeu) === 0) return [];

  const conseils: ConseilLogo[] = [];

  if (!aUnFichier('longueur-sombre')) {
    conseils.push({
      cle: 'longueur-sombre',
      priorite: 1,
      consequence:
        "Votre logo n'existe qu'en version pour fond blanc. Sur les fonds sombres "
        + "de l'application, il serait illisible tel quel : il est donc posé sur une "
        + "plaque claire, qui le sauve mais se remarque au milieu des autres. C'est "
        + "aussi le fichier que réclame la tête du menu de l'univers.",
      ou: [
        "En haut du menu de gauche, une fois dans l'univers",
        "Le bandeau haut de l'espace de travail",
      ],
    });
  }

  if (!aUnFichier('carre-sombre')) {
    conseils.push({
      cle: 'carre-sombre',
      priorite: 2,
      consequence:
        "Aucun blason carré n'est déposé. Les pastilles par lesquelles on ENTRE dans "
        + "l'univers reçoivent donc le fichier disponible, qui n'est pas carré : le "
        + 'dessin y devient tout petit, et le nom qu’il contient illisible.',
      ou: [
        "La pastille de la carte sur l'écran de choix d'espace",
        "La vignette de l'univers dans « Changer d'espace »",
        "L'icône de l'application installée sur téléphone",
      ],
    });
  }

  if (!aUnFichier('carre-clair')) {
    conseils.push({
      cle: 'carre-clair',
      priorite: 3,
      consequence:
        'Pas de blason carré pour les fonds clairs. Les vignettes posées sur un '
        + 'document blanc reprennent un autre fichier.',
      ou: ['Vignette carrée sur un document ou un fond blanc'],
    });
  }

  return conseils;
}

// ============================================================================
// QUELLE VARIANTE POUR QUEL FOND : LA LUMINANCE DÉCIDE, PAS LE SUPPORT
// ----------------------------------------------------------------------------
// DÉFAUT CONSTATÉ LE 08/08/2026, capture du dirigeant à l'appui. L'aperçu
// « en-tête de mail » dessinait le logo sur un aplat ROUGE et annonçait juste
// en dessous « Utilise : en longueur, fond clair ». Les deux ne pouvaient pas
// être vrais en même temps : un rouge saturé est un fond SOMBRE au sens de la
// lisibilité, il réclame donc la variante faite pour le sombre.
//
// La cause était un choix FIGÉ : « c'est un mail, donc fond clair ». C'est la
// nature du support qui décidait, alors que ce qui compte est la couleur
// RÉELLEMENT posée derrière le logo. Un mail peut commencer par un bandeau à la
// couleur de la marque, une icône est un aplat de couleur, un badge aussi.
//
// La règle est donc : on MESURE le fond. Aucun seuil arbitraire n'est inventé,
// on compare simplement lequel du blanc ou du noir ressort le mieux dessus
// (contraste WCAG, `contraste` de color-utils). Si c'est le blanc, le fond
// compte comme sombre.
// ============================================================================

const BLANC = '#FFFFFF';
const NOIR = '#000000';

/**
 * Vrai quand ce fond appelle une variante « fond sombre ».
 *
 * Mesure : le texte blanc ressort-il mieux que le texte noir sur cette couleur ?
 * Sur #434244 le blanc gagne largement, sur #E40233 aussi, sur un jaune vif non.
 */
export function fondEstSombre(fondHex: string): boolean {
  return contraste(fondHex, BLANC) > contraste(fondHex, NOIR);
}

export interface ChoixVariante {
  variante: VarianteLogo;
  /** Le fond mesuré compte-t-il comme sombre. */
  sombre: boolean;
  /**
   * Contraste qui a EMPORTÉ la décision, et contre quoi il a été mesuré.
   * On expose le gagnant plutôt que systématiquement le blanc : sur un fond
   * blanc, « contraste 1:1 avec le blanc » est exact mais ne dit rien, alors
   * que « 21:1 avec le noir » explique la décision.
   */
  contraste: number;
  /** « le blanc » ou « le noir », pour l'écrire dans la phrase. */
  compareA: string;
}

/**
 * La variante à demander pour poser un logo de la forme voulue sur CE fond.
 * Le fond est une couleur réelle (aplat de marque, blanc d'un mail, gris de
 * l'application), jamais une supposition sur le type de support.
 */
export function variantePourFond(fondHex: string, forme: FormeLogo): ChoixVariante {
  const sombre = fondEstSombre(fondHex);
  const variante: VarianteLogo = forme === 'carre'
    ? (sombre ? 'carre-sombre' : 'carre-clair')
    : (sombre ? 'longueur-sombre' : 'longueur-clair');
  return {
    variante,
    sombre,
    contraste: contraste(fondHex, sombre ? BLANC : NOIR),
    compareA: sombre ? 'le blanc' : 'le noir',
  };
}

// ============================================================================
// LA FORME RÉELLE D'UN FICHIER, DITE EN FRANÇAIS
// ----------------------------------------------------------------------------
// DÉFAUT CONSTATÉ LE 08/08/2026. Le dirigeant dépose un fichier de 898 x 1402
// pixels dans les deux emplacements carrés et l'écran répond « Ce fichier n'est
// pas carré : il laissera de larges vides dans une icône. Un fichier 1:1
// tiendra mieux. » Il répond, à juste titre : « J'ai clairement mis un logo au
// format 1:1, tu vois bien que le logo n'est pas en long comme l'autre. »
//
// Sur le FOND l'écran avait raison, 898 x 1402 n'est pas un carré. Sur la FORME
// il avait tort deux fois :
//  - il ne disait pas la forme réelle du fichier. « pas carré » ne se vérifie
//    pas à l'oeil ; « plus HAUT que large, 898 x 1402 » se vérifie tout de suite,
//    et surtout se compare à l'autre fichier, qui est plus large que haut ;
//  - il ne proposait AUCUNE issue. Un constat sans sortie est un reproche.
//
// D'où ce diagnostic : il nomme la forme, dit de quel côté seront les vides,
// tolère le presque-carré, et laisse l'écran proposer de fabriquer le carré.
//
// PLAGE DE TOLÉRANCE RETENUE : de 0,90:1 à 1,11:1 (soit 10 % d'écart dans un
// sens ou dans l'autre). Un fichier de 500 x 540 n'y déclenche rien, et c'est
// justifié : contenu en « contain » dans un carré il laisse 4 % de vide de
// chaque côté, invisible. L'ancien seuil allait de 0,74 à 1,35, ce qui laissait
// passer un 740 x 1000, largement visible dans un masque rond. Il est donc
// resserré, et c'est tenable UNIQUEMENT parce qu'on offre désormais le bouton
// qui fabrique la version carrée : avertir sans issue était le vrai défaut.
// ============================================================================

/** En dessous, le fichier est plus haut que large. */
export const RATIO_CARRE_MIN = 0.9;
/** Au dessus, le fichier est plus large que haut. */
export const RATIO_CARRE_MAX = 1.11;
/** En dessous, un fichier ne remplit pas un cadre d'en-tête en longueur. */
export const RATIO_LONGUEUR_MIN = 1.5;

export type FormeMesuree =
  | 'plus-haut-que-large'
  | 'carre'
  | 'presque-carre'
  | 'un-peu-plus-large'
  | 'en-longueur';

export function mesurerForme(largeur: number, hauteur: number): FormeMesuree {
  const ratio = largeur / hauteur;
  if (ratio < RATIO_CARRE_MIN) return 'plus-haut-que-large';
  if (ratio <= RATIO_CARRE_MAX) {
    return ratio >= 0.98 && ratio <= 1.02 ? 'carre' : 'presque-carre';
  }
  if (ratio < RATIO_LONGUEUR_MIN) return 'un-peu-plus-large';
  return 'en-longueur';
}

/** Nom de la forme, tel qu'on l'écrit à l'écran. */
export function nommerForme(mesuree: FormeMesuree): string {
  switch (mesuree) {
    case 'plus-haut-que-large': return 'plus haut que large';
    case 'carre': return 'carré';
    case 'presque-carre': return 'presque carré';
    case 'un-peu-plus-large': return 'à peine plus large que haut';
    default: return 'en longueur';
  }
}

export interface DiagnosticForme {
  largeur: number;
  hauteur: number;
  ratio: number;
  mesuree: FormeMesuree;
  /** « plus haut que large » */
  nom: string;
  /** « 898 x 1402 px, plus haut que large » */
  resume: string;
  /** Vrai quand la forme convient à l'emplacement, donc aucun avertissement. */
  convient: boolean;
  /** Ce qui se passera vraiment dans le cadre de destination. Null si convient. */
  alerte: string | null;
  /** Vrai quand fabriquer une version carrée réglerait le problème. */
  carreFabricable: boolean;
}

/**
 * Nomme la forme d'un fichier et dit ce qu'elle produira dans l'emplacement.
 * Fonction pure : elle ne connaît ni React ni le réseau, elle est vérifiable
 * seule.
 */
export function diagnostiquerForme(
  largeur: number,
  hauteur: number,
  formeAttendue: FormeLogo,
): DiagnosticForme {
  const ratio = largeur / hauteur;
  const mesuree = mesurerForme(largeur, hauteur);
  const nom = nommerForme(mesuree);
  const dimensions = `${largeur} × ${hauteur} px`;
  const resume = `${dimensions}, ${nom}`;

  let convient: boolean;
  let alerte: string | null = null;

  if (formeAttendue === 'carre') {
    convient = mesuree === 'carre' || mesuree === 'presque-carre';
    if (!convient) {
      // On dit DE QUEL CÔTÉ sont les vides : c'est la seule information qui
      // permette de vérifier le diagnostic d'un coup d'oeil sur l'aperçu.
      const bandes = ratio < 1 ? 'à gauche et à droite' : 'en haut et en bas';
      const rogne = ratio < 1 ? 'le haut et le bas' : 'la gauche et la droite';
      alerte = `Ce fichier est ${nom} (${dimensions}). Posé dans un carré il laissera `
        + `deux bandes vides ${bandes}, et le masque rond d'Android rognera ${rogne} du dessin. `
        + `Le bouton « Fabriquer le carré » en fait une version carrée à partir de ce fichier, `
        + `centrée, sans rien déformer ni rogner.`;
    }
  } else {
    convient = mesuree === 'en-longueur';
    if (!convient) {
      alerte = mesuree === 'plus-haut-que-large'
        ? `Ce fichier est plus haut que large (${dimensions}). Un en-tête de mail ou de facture `
          + `est un bandeau large et bas : le dessin y sera réduit à la hauteur disponible et `
          + `paraîtra minuscule, avec du vide de chaque côté.`
        : `Ce fichier est ${nom} (${dimensions}). Dans un en-tête de mail ou de facture, `
          + `il n'occupera qu'une petite part de la largeur. Un logo avec le nom écrit à côté `
          + `du symbole remplirait mieux le bandeau.`;
    }
  }

  return {
    largeur,
    hauteur,
    ratio,
    mesuree,
    nom,
    resume,
    convient,
    alerte,
    carreFabricable: formeAttendue === 'carre' && !convient,
  };
}
